import { Product } from "@/api/product-api";
import {
  useGetBundleProductComponents,
  useProducts,
} from "@/services/product/useProductService";
import { useRouter } from "next/router";

/**
 * `In the Box` 에 나열할 제품들.
 *
 * 번들을 고른 상태면 **구성품 각각**을 소제목과 함께 보여준다 — prod 동작이고
 * `?bundle=starter-bundle` 이면 `TactSuit Air` · `TactSleeve` 두 묶음이 나온다.
 * 기본 제품이면 그 제품 하나다.
 *
 * ⚠️ 번들 선택의 정본은 **URL(`?bundle=`)** 이다. PDP 가 선택 시 쿼리를 갱신하므로
 * (`[slug].tsx` 의 `replaceQueryParam("bundle", …)`) 여기서 라우터만 읽으면 클릭 선택과
 * 링크 진입이 모두 잡힌다 — prop 을 호출처에 새로 흘리지 않아도 된다.
 *
 * ## 왜 훅으로 뺐나
 *
 * 종전에는 이 계산이 `TactsuitTechSpecs` 안에 있었고, 그래서 **그 컴포넌트를 그리는
 * 분기만** 섹션을 얻었다. DK3 분기는 스펙을 전용 컴포넌트(`Dk3Specs`)로 그려
 * `TactsuitTechSpecs` 를 렌더하지 않으므로 **글러브 PDP 에서 In the Box 가 통째로
 * 없었다** — 데이터(`ProductInTheBoxData[TACTGLOVE_DK3]`)는 있는데도.
 * 계산을 여기 두면 섹션 위치와 무관하게 같은 동작을 쓸 수 있다.
 */
export const useInTheBoxProducts = (product: Product): Product[] => {
  const router = useRouter();
  const bundleSlug =
    typeof router.query.bundle === "string" ? router.query.bundle : null;
  const { data: allProducts = [] } = useProducts();
  const selectedBundle = bundleSlug
    ? (allProducts.find((p) => p.slug === bundleSlug) ?? null)
    : null;
  // ⚠️ 훅은 조건부로 부를 수 없다. 번들이 없으면 base 를 넘기고 **결과를 쓰지 않는다** —
  //    base 로 부른 결과를 그대로 쓰면 글러브처럼 자기 세트를 구성품으로 들고 오는 제품에서
  //    엉뚱한 묶음이 붙는다(`ProductUtil.getBundleComponents` 의 `ownBundleSlug` 분기).
  const bundleComponents = useGetBundleProductComponents(
    selectedBundle ?? product,
  );

  /**
   * ★구성품 중 **이 페이지의 기본 제품 자리**를 `product` 로 바꾼다 — camo 반영.
   *
   * URL 의 번들 slug 는 **라인 대표(비-camo)** 다(`?bundle=starter-bundle&color=camo`). 그래서
   * 구성품을 그대로 쓰면 `TactSuit Air`(비-camo)가 나온다. 반면 `product` 는 PDP 가 이미 색상을
   * 반영해 넘겨준 값이다(`[slug].tsx` 의 `effectiveProduct = isCamo && baseCamo ? baseCamo : …`).
   *
   * 그래서 라우트 slug(=기본 제품)와 같은 구성품만 `product` 로 치환한다. camo 가 아니면
   * 같은 제품이라 무동작이고, `?bundle=starter-bundle-camo` 처럼 이미 camo 구성품이 온
   * 경우에도 slug 가 달라 건드리지 않는다.
   *
   * camo 변형 맵(`CAMO_VARIANT_BY_SLUG`)은 여기서 **필요하지 않다** — 이 훅은 라우트 slug 와
   * 구성품 slug 를 직접 비교(`p.slug === routeSlug ? product : p`)하는 것으로 충분하다.
   *
   * ⚠️ 종전 주석은 *"`[slug].tsx` 지역 상수라 참조하지 않는다"* 였는데, 그 맵이 #5386 에서
   * `@/libs/product/camo-variant` 공유 모듈로 나왔으므로 **그 이유는 더 이상 사실이 아니다.**
   * 참조하지 않는 이유는 접근성이 아니라 **불필요**다 — 필요해지면 공유 모듈을 임포트하면 된다.
   */
  const routeSlug =
    typeof router.query.slug === "string" ? router.query.slug : null;
  const withSelectedColor = (items: Product[]) =>
    items.map((p) => (routeSlug && p.slug === routeSlug ? product : p));

  return selectedBundle
    ? bundleComponents.length > 0
      ? withSelectedColor(bundleComponents)
      : [selectedBundle]
    : [product];
};
